import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Dropdown, Form } from 'react-bootstrap';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { mdiBaseballDiamondOutline } from '@mdi/js';
import { FaRunning, FaFutbol } from 'react-icons/fa';
import Icon from '@mdi/react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Link } from 'react-router-dom';

// Utility function to format phone numbers
const formatPhoneNumber = (phoneNumber) => {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber;
};

// Utility function to get status badge class
const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
        case 'paid':
            return 'bg-success';
        case 'pending':
            return 'bg-warning text-dark';
        case 'cancelled':
            return 'bg-danger';
        default:
            return 'bg-secondary';
    }
};

function CustomTabs({ tabs, activeTab, onSelect }) {
    return (
        <ul className="nav nav-bottom-line align-items-center justify-content-center justify-content-md-start mb-0 border-0">
            {tabs.map((tab) => (
                <li className="nav-item" key={tab.eventKey}>
                    <button
                        type="button"
                        className={`nav-link border-0 bg-transparent ${activeTab === tab.eventKey ? 'active' : ''}`}
                        onClick={() => onSelect(tab.eventKey)}
                    >
                        {tab.title}
                    </button>
                </li>
            ))}
        </ul>
    );
}

function ManagePlayerDetailPage() {
    const { playerId } = useParams();
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('about');
    const [isEditing, setIsEditing] = useState(false);
    const [editedBio, setEditedBio] = useState('');
    const [isEditingBirthdate, setIsEditingBirthdate] = useState(false);
    const [editedBirthdate, setEditedBirthdate] = useState(null);
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const [editedPhone, setEditedPhone] = useState('');
    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [editedEmail, setEditedEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await axios.get(`/api/users/user-detail/${playerId}/`, {
                    withCredentials: true,
                });
                setPlayer(response.data);
                // console.log(response.data);
                setError(null);
            } catch (error) {
                console.error('Error fetching user data:', error);
                setError('Failed to load user data');
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [playerId]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div className="text-danger">{error}</div>;
    }

    const tabs = [
        { eventKey: 'about', title: 'About' },
        { eventKey: 'teams', title: 'Teams' },
        { eventKey: 'games', title: 'Games' },
        { eventKey: 'stats', title: 'Stats' },
        { eventKey: 'orders', title: 'Orders' },
    ];

    const handleEditBio = () => {
        setEditedBio(player.bio || '');
        setIsEditing(true);
    };

    const handleSaveBio = async () => {
        try {
            const requestData = {
                bio: editedBio || ''
            };

            const response = await axios.patch(`/api/users/user-detail/${playerId}/`, requestData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            setPlayer(response.data);
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating bio:', error);
            if (error.response) {
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);
            }
            setError('Failed to update bio');
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedBio('');
    };

    const handleEditBirthdate = () => {
        setEditedBirthdate(player.birthdate ? new Date(player.birthdate) : null);
        setIsEditingBirthdate(true);
    };

    const handleSaveBirthdate = async () => {
        try {
            const requestData = {
                birthdate: editedBirthdate ? editedBirthdate.toISOString().split('T')[0] : ''
            };

            const response = await axios.patch(`/api/users/user-detail/${playerId}/`, requestData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            setPlayer(response.data);
            setIsEditingBirthdate(false);
        } catch (error) {
            console.error('Error updating birthdate:', error);
            if (error.response) {
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);
            }
            setError('Failed to update birthdate');
        }
    };

    const handleCancelBirthdateEdit = () => {
        setIsEditingBirthdate(false);
        setEditedBirthdate(null);
    };

    const handleEditPhone = () => {
        setEditedPhone(player.phone_number || '');
        setIsEditingPhone(true);
    };

    const handleSavePhone = async () => {
        try {
            const formattedPhone = formatPhoneNumber(editedPhone);
            const requestData = {
                phone_number: formattedPhone
            };

            const response = await axios.patch(`/api/users/user-detail/${playerId}/`, requestData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            setPlayer(response.data);
            setIsEditingPhone(false);
        } catch (error) {
            console.error('Error updating phone:', error);
            setError('Failed to update phone number');
        }
    };

    const handleCancelPhoneEdit = () => {
        setIsEditingPhone(false);
        setEditedPhone('');
    };

    const handleEditEmail = () => {
        setEditedEmail(player.email || '');
        setIsEditingEmail(true);
        setEmailError('');
    };

    const handleSaveEmail = async () => {
        try {
            // First check if email is already taken
            const checkResponse = await axios.post('/api/users/check-email/', {
                email: editedEmail,
                exclude_user_id: playerId
            }, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (checkResponse.data.exists) {
                setEmailError('This email is already in use');
                return;
            }

            const requestData = {
                email: editedEmail
            };

            const response = await axios.patch(`/api/users/user-detail/${playerId}/`, requestData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            setPlayer(response.data);
            setIsEditingEmail(false);
            setEmailError('');
        } catch (error) {
            console.error('Error updating email:', error);
            if (error.response?.data?.email) {
                setEmailError(error.response.data.email[0]);
            } else {
                setError('Failed to update email');
            }
        }
    };

    const handleCancelEmailEdit = () => {
        setIsEditingEmail(false);
        setEditedEmail('');
        setEmailError('');
    };



    return (
        <Container>
            <Row className="g-4">
                <Col lg={8} className="vstack gap-4">
                    <Card>
                        <div className="h-200px rounded-top" style={{ backgroundImage: `url(${`/static/images/kb_static/lemont_rec_league_banner.png`})`, backgroundPosition: 'center', backgroundSize: 'cover', backgroundRepeat: 'no-repeat' }}></div>
                        <Card.Body className="py-0">
                            <div className="d-sm-flex align-items-start text-center text-sm-start">
                                <div>
                                    <div className="avatar avatar-xxl mt-n5 mb-3">
                                        <img className="avatar-img rounded-circle border border-white border-3" src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                            alt={`${player.first_name} ${player.last_name}`} />
                                    </div>
                                </div>
                                <div className="ms-sm-4 mt-sm-3">
                                    <h1 className="mb-0 h5">{player.first_name} {player.last_name} <i className="bi bi-patch-check-fill text-success small"></i></h1>
                                    <p>{player.email}</p>
                                </div>
                                <div className="d-flex mt-3 justify-content-center ms-sm-auto">
                                    <Dropdown>
                                        <Dropdown.Toggle bsPrefix="dropdown-icon" variant="light" id="profileAction2">
                                            <i className="bi bi-three-dots"></i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item href="#"> <i className="bi bi-bookmark fa-fw pe-2"></i>Share profile in a message</Dropdown.Item>
                                            <Dropdown.Item href="#"> <i className="bi bi-file-earmark-pdf fa-fw pe-2"></i>Save your profile to PDF</Dropdown.Item>
                                            <Dropdown.Item href="#"> <i className="bi bi-lock fa-fw pe-2"></i>Lock profile</Dropdown.Item>
                                            <Dropdown.Divider />
                                            <Dropdown.Item href="#"> <i className="bi bi-gear fa-fw pe-2"></i>Profile settings</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                            <ul className="list-inline mb-0 text-center text-sm-start mt-3 mt-sm-0">
                                <li className="list-inline-item me-3">
                                    {player.roles.includes('COMMISSIONER') ? (
                                        <Icon className="me-1" path={mdiBaseballDiamondOutline} size={1} />
                                    ) : (
                                        <FaRunning className="me-1" />
                                    )} {player.roles}
                                </li>
                                <li className="list-inline-item"><i className="bi bi-calendar2-plus me-1"></i> Joined on {player.date_joined}</li>
                            </ul>
                        </Card.Body>
                        <Card.Footer className="mt-3 pt-2 pb-0">
                            <CustomTabs tabs={tabs} activeTab={activeTab} onSelect={setActiveTab} />
                        </Card.Footer>
                    </Card>
                    <Card className="mb-4">
                        <Card.Header className="border-0 pb-0">
                            <h5 className="card-title">{tabs.find(tab => tab.eventKey === activeTab).title}</h5>
                        </Card.Header>
                        <Card.Body>
                            {activeTab === 'about' && (
                                <>
                                    <div className="rounded border px-3 py-2 mb-3">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <h6 className="mb-0">Overview</h6>
                                            {!isEditing ? (
                                                <Dropdown>
                                                    <Dropdown.Toggle
                                                        bsPrefix="dropdown-icon"
                                                        variant="link"
                                                        className="text-secondary mb-0">
                                                        <i className="bi bi-three-dots"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={handleEditBio}>
                                                            <i className="bi bi-pencil-square fa-fw pe-2"></i>Edit
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            ) : (
                                                <div className="d-flex gap-2">
                                                    <button
                                                        onClick={handleCancelEdit}
                                                        className="btn btn-link text-secondary p-0 hover-red-bg rounded-circle"
                                                        style={{
                                                            fontSize: '0.9rem',
                                                            width: '24px',
                                                            height: '24px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                    </button>
                                                    <button
                                                        onClick={handleSaveBio}
                                                        className="btn btn-link text-secondary p-0 hover-green-bg rounded-circle"
                                                        style={{
                                                            fontSize: '0.9rem',
                                                            width: '24px',
                                                            height: '24px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <i className="bi bi-check-lg"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {isEditing ? (
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={editedBio}
                                                onChange={(e) => setEditedBio(e.target.value)}
                                                placeholder="Enter your bio..."
                                                className="border-0 p-0 bg-transparent"
                                                style={{ resize: 'none' }}
                                            />
                                        ) : (
                                            <p className="mb-0">{player.bio || 'No bio available'}</p>
                                        )}
                                    </div>
                                    <Row className="g-4">
                                        <Col sm={6}>
                                            <div className="d-flex align-items-center rounded border px-3 py-2">
                                                <p className="mb-0">
                                                    {player.roles === 'COMMISSIONER' ? (
                                                        <Icon className="me-2" path={mdiBaseballDiamondOutline} size={1} />
                                                    ) : (
                                                        <FaRunning className="me-2" />
                                                    )} Role: <strong>{player.roles} </strong>
                                                </p>
                                                <Dropdown className="ms-auto">
                                                    <Dropdown.Toggle bsPrefix="dropdown-icon" variant="link" className="text-secondary mb-0">
                                                        <i className="bi bi-three-dots"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item href="#"> <i className="bi bi-pencil-square fa-fw pe-2"></i>Edit</Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-center rounded border px-3 py-2">
                                                <p className="mb-0">
                                                    <FaFutbol className="me-2" /> League: <strong> {player.players[0].league?.name} </strong>
                                                </p>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-center rounded border px-3 py-2">
                                                {isEditingBirthdate ? (
                                                    <div className="d-flex align-items-center w-100">
                                                        <div className="d-flex align-items-center">
                                                            <i className="bi bi-calendar-date fa-fw me-2"></i> Born: <span className="ms-1">
                                                                <DatePicker
                                                                    selected={editedBirthdate}
                                                                    onChange={(date) => setEditedBirthdate(date)}
                                                                    dateFormat="MMMM d, yyyy"
                                                                    className="form-control border-0 p-0 bg-transparent"
                                                                    placeholderText="Select date"
                                                                    showPopperArrow={false}
                                                                    calendarClassName="border-0 shadow"
                                                                />
                                                            </span>
                                                        </div>
                                                        <div className="d-flex gap-2 ms-2">
                                                            <button
                                                                onClick={handleCancelBirthdateEdit}
                                                                className="btn btn-link text-secondary p-0 hover-red-bg rounded-circle"
                                                                style={{
                                                                    fontSize: '0.9rem',
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <i className="bi bi-x-lg"></i>
                                                            </button>
                                                            <button
                                                                onClick={handleSaveBirthdate}
                                                                className="btn btn-link text-secondary p-0 hover-green-bg rounded-circle"
                                                                style={{
                                                                    fontSize: '0.9rem',
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <i className="bi bi-check-lg"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center w-100">
                                                        <span className="d-flex align-items-center">
                                                            <i className="bi bi-calendar-date fa-fw me-2"></i> Born: <strong className="ms-1">{player.birthdate}</strong>
                                                        </span>
                                                        <Dropdown className="ms-auto">
                                                            <Dropdown.Toggle
                                                                bsPrefix="dropdown-icon"
                                                                variant="link"
                                                                className="text-secondary mb-0">
                                                                <i className="bi bi-three-dots"></i>
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item onClick={handleEditBirthdate}>
                                                                    <i className="bi bi-pencil-square fa-fw pe-2"></i>Edit
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-center rounded border px-3 py-2">
                                                <p className="mb-0">
                                                    <i className="bi bi-geo-alt fa-fw me-2"></i> Joined on: <strong> {player.date_joined} </strong>
                                                </p>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-center rounded border px-3 py-2">
                                                {isEditingPhone ? (
                                                    <div className="d-flex align-items-center w-100">
                                                        <div className="d-flex align-items-center">
                                                            <i className="bi bi-telephone fa-fw me-2"></i> Phone: <span className="ms-1">
                                                                <Form.Control
                                                                    type="tel"
                                                                    value={editedPhone}
                                                                    onChange={(e) => setEditedPhone(e.target.value)}
                                                                    className="border-0 p-0 bg-transparent"
                                                                    placeholder="(555) 555-5555"
                                                                />
                                                            </span>
                                                        </div>
                                                        <div className="d-flex gap-2 ms-2">
                                                            <button
                                                                onClick={handleCancelPhoneEdit}
                                                                className="btn btn-link text-secondary p-0 hover-red-bg rounded-circle"
                                                                style={{
                                                                    fontSize: '0.9rem',
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <i className="bi bi-x-lg"></i>
                                                            </button>
                                                            <button
                                                                onClick={handleSavePhone}
                                                                className="btn btn-link text-secondary p-0 hover-green-bg rounded-circle"
                                                                style={{
                                                                    fontSize: '0.9rem',
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <i className="bi bi-check-lg"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center w-100">
                                                        <span className="d-flex align-items-center">
                                                            <i className="bi bi-telephone fa-fw me-2"></i> Phone: <strong className="ms-1">{player.phone_number}</strong>
                                                        </span>
                                                        <Dropdown className="ms-auto">
                                                            <Dropdown.Toggle
                                                                bsPrefix="dropdown-icon"
                                                                variant="link"
                                                                className="text-secondary mb-0">
                                                                <i className="bi bi-three-dots"></i>
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item onClick={handleEditPhone}>
                                                                    <i className="bi bi-pencil-square fa-fw pe-2"></i>Edit
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-center rounded border px-3 py-2">
                                                {isEditingEmail ? (
                                                    <div className="d-flex align-items-center w-100">
                                                        <div className="d-flex align-items-center">
                                                            <i className="bi bi-envelope fa-fw me-2"></i> Email: <span className="ms-1">
                                                                <Form.Control
                                                                    type="email"
                                                                    value={editedEmail}
                                                                    onChange={(e) => setEditedEmail(e.target.value)}
                                                                    className={`border-0 p-0 bg-transparent ${emailError ? 'is-invalid' : ''}`}
                                                                    placeholder="email@example.com"
                                                                />
                                                            </span>
                                                        </div>
                                                        <div className="d-flex gap-2 ms-2">
                                                            <button
                                                                onClick={handleCancelEmailEdit}
                                                                className="btn btn-link text-secondary p-0 hover-red-bg rounded-circle"
                                                                style={{
                                                                    fontSize: '0.9rem',
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <i className="bi bi-x-lg"></i>
                                                            </button>
                                                            <button
                                                                onClick={handleSaveEmail}
                                                                className="btn btn-link text-secondary p-0 hover-green-bg rounded-circle"
                                                                style={{
                                                                    fontSize: '0.9rem',
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <i className="bi bi-check-lg"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center w-100">
                                                        <span className="d-flex align-items-center">
                                                            <i className="bi bi-envelope fa-fw me-2"></i> Email: <strong className="ms-1">{player.email}</strong>
                                                        </span>
                                                        <Dropdown className="ms-auto">
                                                            <Dropdown.Toggle
                                                                bsPrefix="dropdown-icon"
                                                                variant="link"
                                                                className="text-secondary mb-0">
                                                                <i className="bi bi-three-dots"></i>
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item onClick={handleEditEmail}>
                                                                    <i className="bi bi-pencil-square fa-fw pe-2"></i>Edit
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                )}
                                            </div>
                                            {emailError && (
                                                <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                                                    {emailError}
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </>
                            )}
                            {activeTab === 'teams' && (
                                <ul className="list-group">
                                    {player.teams.map((team) => (
                                        <li
                                            key={team.id}
                                            className="list-group-item d-flex justify-content-between align-items-center"
                                        >
                                            <div className="d-flex align-items-center">
                                                <div className="me-3" style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                                                    <img
                                                        src={team.logo || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                        alt={`${team.name} logo`}
                                                        className="img-fluid rounded-circle"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            border: '1px solid #ddd'
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <strong>
                                                        <Link
                                                            to={`/league-manager/teams/${team.id}/`}
                                                            className="text-decoration-none"
                                                        >
                                                            {team.name}
                                                        </Link>
                                                    </strong>
                                                    <br />
                                                    <small>
                                                        {team.league?.name ? team.league.name : 'No league assigned'} •
                                                        {team.captain_name ? ` Captain: ${team.captain_name}` : ' No captain assigned'}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="me-2"
                                                    style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        backgroundColor: team.color,
                                                        borderRadius: '50%',
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                <span className="badge bg-primary rounded-pill">
                                                    {team.total_games} Games
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                    {(!player.teams || player.teams.length === 0) && (
                                        <li className="list-group-item text-center text-muted">
                                            No teams found for this player
                                        </li>
                                    )}
                                </ul>
                            )}
                            {activeTab === 'games' && (
                                <div className="rounded border px-3 py-2 mb-3">
                                    <p>Content for {tabs.find(tab => tab.eventKey === activeTab).title} goes here.</p>
                                </div>
                            )}
                            {activeTab === 'stats' && (
                                <div className="rounded border px-3 py-2 mb-3">
                                    <p>Content for {tabs.find(tab => tab.eventKey === activeTab).title} goes here.</p>
                                </div>
                            )}
                            {activeTab === 'orders' && (
                                <div>
                                    {player.orders && player.orders.length > 0 ? (
                                        <div className="row g-4">
                                            {player.orders.map((order) => (
                                                <div key={order.id} className="col-12">
                                                    <Card className="border-0 shadow-sm">
                                                        <Card.Header className="bg-light border-0">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <h6 className="mb-1 fw-bold">
                                                                        Order #{order.order_number}
                                                                    </h6>
                                                                    <small className="text-muted">
                                                                        {new Date(order.created_at).toLocaleDateString('en-US', {
                                                                            year: 'numeric',
                                                                            month: 'long',
                                                                            day: 'numeric',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </small>
                                                                </div>
                                                                <div className="text-end">
                                                                    <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                                                                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                                                    </span>
                                                                    <div className="mt-1">
                                                                        <strong className="text-primary">
                                                                            ${parseFloat(order.total_amount).toFixed(2)}
                                                                        </strong>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Card.Header>
                                                        <Card.Body className="p-0">
                                                            {order.items && order.items.length > 0 ? (
                                                                <div className="list-group list-group-flush">
                                                                    {(() => {
                                                                        // Separate product items and charge items
                                                                        const productItems = order.items.filter(item => item.item_type === 'product');
                                                                        const chargeItems = order.items.filter(item => item.item_type === 'charge');

                                                                        const displayItems = [];

                                                                        // Add product items
                                                                        productItems.forEach((item, index) => {
                                                                            displayItems.push(
                                                                                <div key={`product-${index}`} className="list-group-item border-0 px-4 py-3">
                                                                                    <div className="d-flex align-items-center">
                                                                                        <div className="me-3" style={{ width: '50px', height: '50px', flexShrink: 0 }}>
                                                                                            <img
                                                                                                src={item.product?.image || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                                                                alt={item.product?.name || 'Product'}
                                                                                                className="img-fluid rounded"
                                                                                                style={{
                                                                                                    width: '100%',
                                                                                                    height: '100%',
                                                                                                    objectFit: 'cover',
                                                                                                    border: '1px solid #eee'
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                        <div className="flex-grow-1">
                                                                                            <h6 className="mb-1 fw-semibold">
                                                                                                {item.product?.name || 'Product'}
                                                                                            </h6>
                                                                                            {item.category && (
                                                                                                <small className="text-muted d-block">
                                                                                                    {item.category}
                                                                                                </small>
                                                                                            )}
                                                                                            <div className="d-flex align-items-center mt-1">
                                                                                                <span className="badge bg-light text-dark me-2">
                                                                                                    Qty: {item.quantity}
                                                                                                </span>
                                                                                                <span className="text-success fw-semibold">
                                                                                                    ${parseFloat(item.price).toFixed(2)} each
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="text-end">
                                                                                            <div className="fw-bold text-primary">
                                                                                                ${(parseFloat(item.price) * item.quantity).toFixed(2)}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        });

                                                                        // Add combined charge items as one line item
                                                                        if (chargeItems.length > 0) {
                                                                            const totalCharges = chargeItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
                                                                            displayItems.push(
                                                                                <div key="charges" className="list-group-item border-0 px-4 py-3">
                                                                                    <div className="d-flex align-items-center">
                                                                                        <div className="me-3" style={{ width: '50px', height: '50px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                            <i className="bi bi-credit-card text-primary" style={{ fontSize: '1.5rem' }}></i>
                                                                                        </div>
                                                                                        <div className="flex-grow-1">
                                                                                            <h6 className="mb-1 fw-semibold">
                                                                                                Taxes and Fees
                                                                                            </h6>
                                                                                            <small className="text-muted d-block">
                                                                                                Processing fees and applicable taxes
                                                                                            </small>
                                                                                        </div>
                                                                                        <div className="text-end">
                                                                                            <div className="fw-bold text-primary">
                                                                                                ${totalCharges.toFixed(2)}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }

                                                                        return displayItems;
                                                                    })()}
                                                                </div>
                                                            ) : (
                                                                <div className="p-4 text-center text-muted">
                                                                    <i className="bi bi-box-seam fs-1 mb-3"></i>
                                                                    <p className="mb-0">No items found for this order</p>
                                                                </div>
                                                            )}
                                                        </Card.Body>
                                                        <Card.Footer className="bg-light border-0">
                                                            <div className="row text-muted small">
                                                                <div className="col-6">
                                                                    <div className="d-flex justify-content-between mb-1">
                                                                        <span>Subtotal:</span>
                                                                        <span>${parseFloat(order.subtotal).toFixed(2)}</span>
                                                                    </div>
                                                                    {parseFloat(order.fee_total) > 0 && (
                                                                        <div className="d-flex justify-content-between mb-1">
                                                                            <span>Fees:</span>
                                                                            <span>${parseFloat(order.fee_total).toFixed(2)}</span>
                                                                        </div>
                                                                    )}
                                                                    {parseFloat(order.tax_total) > 0 && (
                                                                        <div className="d-flex justify-content-between mb-1">
                                                                            <span>Tax:</span>
                                                                            <span>${parseFloat(order.tax_total).toFixed(2)}</span>
                                                                        </div>
                                                                    )}
                                                                    {parseFloat(order.shipping_total) > 0 && (
                                                                        <div className="d-flex justify-content-between mb-1">
                                                                            <span>Shipping:</span>
                                                                            <span>${parseFloat(order.shipping_total).toFixed(2)}</span>
                                                                        </div>
                                                                    )}
                                                                    {parseFloat(order.discount_total) > 0 && (
                                                                        <div className="d-flex justify-content-between mb-1">
                                                                            <span>Discount:</span>
                                                                            <span className="text-success">-${parseFloat(order.discount_total).toFixed(2)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="col-6">
                                                                    <div className="d-flex justify-content-between fw-bold text-dark">
                                                                        <span>Total:</span>
                                                                        <span className="text-primary">${parseFloat(order.total_amount).toFixed(2)}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Card.Footer>
                                                    </Card>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="bi bi-cart-x fs-1 text-muted mb-3"></i>
                                            <h5 className="text-muted mb-2">No Orders Found</h5>
                                            <p className="text-muted mb-0">This player hasn't placed any orders yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                            <style>
                                {`
                                    .hover-red-bg:hover {
                                        background-color: #dc3545 !important;
                                        color: white !important;
                                        transition: all 0.2s ease;
                                    }
                                    .hover-green-bg:hover {
                                        background-color: #198754 !important;
                                        color: white !important;
                                        transition: all 0.2s ease;
                                    }
                                `}
                            </style>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default ManagePlayerDetailPage;